import React, { useState } from 'react';
import { Modal, Button, Text, Stack, Alert, Textarea, Select, TextInput, Group } from '@mantine/core';
import { AlertTriangle, Trash2, Send } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { userApi, DeletionFeedback } from '../api/userApi';
import { useUser } from '../contexts/UserContext';

interface DeleteAccountModalProps {
  opened: boolean;
  onClose: () => void;
}

type DeletionStep = 'warning' | 'feedback' | 'confirmed';

const DELETION_REASONS = [
  { value: 'not-using', label: "I'm not using it anymore" },
  { value: 'too-expensive', label: "It's too expensive" },
  { value: 'missing-features', label: "Missing features I need" },
  { value: 'found-alternative', label: "Found a better alternative" },
  { value: 'privacy-concerns', label: "Privacy concerns" },
  { value: 'too-complicated', label: "Too complicated to use" },
  { value: 'other', label: "Other reason" },
];

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ opened, onClose }) => {
  const { currentUser, logout } = useUser();
  const [step, setStep] = useState<DeletionStep>('warning');
  const [feedback, setFeedback] = useState<DeletionFeedback>({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    reason: '',
    message: '',
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => userApi.deleteAccount(currentUser?.email || ''),
    onSuccess: () => {
      setStep('feedback');
    },
    onError: (error: Error) => {
      console.error('Failed to delete account:', error);
    },
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: (feedbackData: DeletionFeedback) => userApi.submitDeletionFeedback(feedbackData),
    onSuccess: async () => {
      // Logout and redirect
      await logout();
      window.location.href = 'https://symphonytodo.com';
    },
    onError: (error: Error) => {
      console.error('Failed to submit feedback:', error);
      // Still redirect even if feedback fails
      handleSkipFeedback();
    },
  });

  const handleDeleteConfirm = () => {
    if (!currentUser?.email) return;
    deleteAccountMutation.mutate();
  };

  const handleSubmitFeedback = () => {
    if (!feedback.reason) {
      return; // Validation handled by form
    }
    submitFeedbackMutation.mutate(feedback);
  };

  const handleSkipFeedback = async () => {
    await logout();
    window.location.href = 'https://symphonytodo.com';
  };

  const handleClose = () => {
    setStep('warning');
    setFeedback({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      reason: '',
      message: '',
    });
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <Trash2 size={20} color="var(--mantine-color-red-6)" />
          <Text fw={600}>Delete Account</Text>
        </Group>
      }
      size="md"
      styles={{
        header: {
          backgroundColor: 'var(--app-surface)',
          borderBottom: '1px solid var(--app-border)',
        },
        title: {
          color: 'var(--app-text-primary)',
        },
        content: {
          backgroundColor: 'var(--app-surface)',
        },
        body: {
          backgroundColor: 'var(--app-surface)',
          color: 'var(--app-text-primary)',
        },
      }}
    >
      {step === 'warning' && (
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={20} />} color="red" title="Warning: This action is permanent">
            <Text size="sm">
              Deleting your account will permanently remove:
            </Text>
            <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>All your todo lists and tasks</li>
              <li>All comments and subtasks</li>
              <li>All notes and attachments</li>
              <li>All settings and preferences</li>
              <li>All collaboration history</li>
            </ul>
            <Text size="sm" fw={600} c="red">
              This action cannot be undone.
            </Text>
          </Alert>

          {deleteAccountMutation.isError && (
            <Alert color="red" title="Error">
              {deleteAccountMutation.error?.message || 'Failed to delete account. Please try again.'}
            </Alert>
          )}

          <Text size="sm" c="dimmed">
            Are you sure you want to permanently delete your account?
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              loading={deleteAccountMutation.isPending}
              leftSection={<Trash2 size={16} />}
            >
              Yes, Delete My Account
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'feedback' && (
        <Stack gap="md">
          <Alert color="green" title="Account Deleted">
            <Text size="sm">
              Your account and all associated data have been permanently deleted.
            </Text>
            <Text size="sm" mt="xs" fw={500}>
              It's been a pleasure having you as a customer, and you're welcome back anytime! 🙏
            </Text>
          </Alert>

          <Text size="sm" fw={600}>
            Help us improve (optional)
          </Text>

          <Text size="sm" c="dimmed">
            We'd love to hear why you're leaving. Your feedback helps us improve SymphonyTodo for everyone.
          </Text>

          <TextInput
            label="Name"
            placeholder="Your name (optional)"
            value={feedback.name}
            onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
          />

          <TextInput
            label="Email"
            placeholder="Your email (optional)"
            value={feedback.email}
            onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
          />

          <Select
            label="What was your primary reason for leaving?"
            placeholder="Select a reason"
            data={DELETION_REASONS}
            value={feedback.reason}
            onChange={(value) => setFeedback({ ...feedback, reason: value || '' })}
            required
            withAsterisk
          />

          <Textarea
            label="Additional feedback"
            placeholder="Tell us more about your experience... (optional)"
            value={feedback.message}
            onChange={(e) => setFeedback({ ...feedback, message: e.target.value })}
            minRows={4}
            maxRows={8}
          />

          {submitFeedbackMutation.isError && (
            <Alert color="red" title="Error">
              Failed to submit feedback, but you'll still be logged out.
            </Alert>
          )}

          <Group justify="space-between" mt="md">
            <Button
              variant="subtle"
              onClick={handleSkipFeedback}
              disabled={submitFeedbackMutation.isPending}
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmitFeedback}
              loading={submitFeedbackMutation.isPending}
              disabled={!feedback.reason}
              leftSection={<Send size={16} />}
            >
              Submit & Continue
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
